<template>
  <div class="rawang-kpi">
    <div class="container">
      <div class="header">
        <h1>Rawang Hospital KPI</h1>
        <p>Finance can maintain the top KPI tiles used in the Rawang Management dashboard.</p>
      </div>

      <div class="card">
        <div class="card-header">
          <div class="header-left">
            <div class="header-icon">
              <i class="fa-solid fa-hospital"></i>
            </div>
            <div>
              <h2>Snapshot by Date</h2>
              <p>Select a date to view or edit the KPI snapshot for that day.</p>
            </div>
          </div>
        </div>

        <div class="form-grid">
          <div class="form-row">
            <label for="kpi-date">KPI Date</label>
            <input
              id="kpi-date"
              type="date"
              v-model="kpiDate"
              @change="loadSnapshot"
            >
          </div>
        </div>

        <div v-if="loading" class="loading-row">
          <i class="fa-solid fa-spinner fa-spin"></i>
          <span>Loading KPI snapshot...</span>
        </div>

        <div class="sections" v-else>
          <!-- Financial -->
          <section class="kpi-section">
            <h3>Financial</h3>
            <div class="kpi-grid">
              <div class="field">
                <label>Revenue (RM)</label>
                <input type="number" v-model.number="form.revenue" min="0" step="0.01">
              </div>
              <div class="field">
                <label>Operating Expenses (RM)</label>
                <input type="number" v-model.number="form.operating_expenses" min="0" step="0.01">
              </div>
              <div class="field">
                <label>EBITDA (RM)</label>
                <input type="number" v-model.number="form.ebitda" min="0" step="0.01">
              </div>
              <div class="field">
                <label>EBITDA Margin (%)</label>
                <input type="number" v-model.number="form.ebitda_margin" min="0" max="100" step="0.1">
              </div>
            </div>
          </section>

          <section class="kpi-section">
            <h3>OPD/ADM & New OPV Ratio</h3>
            <div class="kpi-grid">
              <div class="field">
                <label>OPD / ADM</label>
                <input type="number" v-model.number="form.opd_per_adm" min="0" step="0.1">
              </div>
              <div class="field">
                <label>New OPV / Total OPV (%)</label>
                <input type="number" v-model.number="form.new_opv_ratio" min="0" max="100" step="0.1">
              </div>
            </div>
          </section>
        </div>

        <div class="actions">
          <button
            class="save-btn"
            :disabled="saving || !kpiDate"
            @click="saveSnapshot"
          >
            <i v-if="saving" class="fa-solid fa-spinner fa-spin"></i>
            <i v-else class="fa-solid fa-save"></i>
            <span>{{ saving ? 'Saving...' : 'Save KPI Snapshot' }}</span>
          </button>
        </div>

        <div v-if="message" :class="['message', message.type]">
          <i :class="message.type === 'success' ? 'fa-solid fa-check-circle' : 'fa-solid fa-circle-exclamation'"></i>
          <span>{{ message.text }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import axios from 'axios'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: import.meta.env.VITE_API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json'
  }
})

api.interceptors.request.use(config => {
  const token = sessionStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

const today = new Date()
const kpiDate = ref(today.toISOString().slice(0, 10))
const loading = ref(false)
const saving = ref(false)
const message = ref(null)

const form = reactive({
  revenue: 0,
  operating_expenses: 0,
  ebitda: 0,
  ebitda_margin: 0,
  opd_per_adm: 0,
  new_opv_ratio: 0
})

function setFormFromSnapshot (snapshot) {
  form.revenue = Number(snapshot.revenue || 0)
  form.operating_expenses = Number(snapshot.operating_expenses || 0)
  form.ebitda = Number(snapshot.ebitda || 0)
  form.ebitda_margin = Number(snapshot.ebitda_margin || 0)
  form.opd_per_adm = Number(snapshot.opd_per_adm || 0)
  form.new_opv_ratio = Number(snapshot.new_opv_ratio || 0)
}

async function loadSnapshot () {
  if (!kpiDate.value) return
  loading.value = true
  message.value = null
  try {
    const { data } = await api.get('/api/finance/rawang-kpi', {
      params: { date: kpiDate.value }
    })
    setFormFromSnapshot(data)
  } catch (error) {
    // If 404, just reset to zeros
    if (error.response?.status === 404) {
      setFormFromSnapshot({})
    } else {
      message.value = {
        type: 'error',
        text: 'Failed to load KPI snapshot.'
      }
    }
  } finally {
    loading.value = false
  }
}

async function saveSnapshot () {
  if (!kpiDate.value) return
  saving.value = true
  message.value = null
  try {
    const payload = {
      kpi_date: kpiDate.value,
      ...form
    }
    const { data } = await api.post('/api/finance/rawang-kpi', payload)
    setFormFromSnapshot(data)
    message.value = {
      type: 'success',
      text: 'KPI snapshot saved successfully.'
    }
  } catch (error) {
    message.value = {
      type: 'error',
      text: error.response?.data?.error || 'Failed to save KPI snapshot.'
    }
  } finally {
    saving.value = false
  }
}

onMounted(() => {
  loadSnapshot()
})
</script>

<style scoped>
.rawang-kpi {
  padding: 24px;
  background: #f8fafc;
  min-height: 100vh;
}

.container {
  width: 100%;
  margin: 0 auto;
}

.header {
  margin-bottom: 24px;
}

.header h1 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px 0;
}

.header p {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0;
}

.card {
  background: #ffffff;
  border-radius: 12px;
  padding: 20px 20px 24px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
  border: 1px solid rgba(226, 232, 240, 0.9);
}

.card-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-icon {
  width: 40px;
  height: 40px;
  border-radius: 999px;
  background: #e0f2fe;
  color: #0ea5e9;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-header h2 {
  font-size: 1rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 2px 0;
}

.card-header p {
  font-size: 0.75rem;
  color: #64748b;
  margin: 0;
}

.form-grid {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.form-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 200px;
}

.form-row label {
  font-size: 0.8rem;
  color: #475569;
  font-weight: 500;
}

.form-row input[type="date"] {
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  font-size: 0.85rem;
  color: #0f172a;
}

.sections {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.kpi-section h3 {
  font-size: 0.9rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 8px 0;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px 16px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.field label {
  font-size: 0.75rem;
  color: #64748b;
}

.field input[type="number"] {
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  font-size: 0.8rem;
  color: #0f172a;
}

.field input[type="number"]:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.loading-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: #64748b;
  margin: 8px 0 4px;
}

.actions {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}

.save-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 6px;
  border: none;
  background: #16a34a;
  color: #ffffff;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;
}

.save-btn:disabled {
  background: #e2e8f0;
  color: #94a3b8;
  cursor: not-allowed;
}

.save-btn:not(:disabled):hover {
  background: #15803d;
}

.message {
  margin-top: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
}

.message.success {
  background: #dcfce7;
  color: #166534;
  border: 1px solid #bbf7d0;
}

.message.error {
  background: #fee2e2;
  color: #b91c1c;
  border: 1px solid #fecaca;
}

.message i {
  font-size: 1rem;
}

@media (max-width: 768px) {
  .rawang-kpi {
    padding: 12px;
  }

  .card {
    padding: 14px 12px 16px;
  }
}
</style>

